import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import classnames from 'classnames';

import Form from 'components/form/Form';
import Spinner from 'components/spinner/Spinner';
import Popover from 'components/popover/Popover';
import { withModal } from 'components/modal/ModalHOC';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButton from 'components/buttons/action-button/ActionButton';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

import BaseReportingPage from '../base/BaseReportingPage';
import CCACreatorSearch from '../../components/cca-creator-search/CCACreatorSearch';
import CCAMetricsSummary from '../../components/cca-metrics-summary/CCAMetricsSummary';
import CostAttributionTile from '../../components/cost-attribution-tile/CostAttributionTile';
import CostAttributionBulkModal from '../../components/cost-attribution-bulk-modal/CostAttributionBulkModal';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchSplits, resetSplits, updateSplits } from '../../reducers/campaign-cost-attribution';
import { fetchMetaInfo } from 'modules/reporting/reducers/meta-info';

import { useScrollTo } from 'hooks/use-scroll-to';

import { autoscrollToRequired } from 'utils/page';
import { __t, applyTranslations } from 'utils/language';
import { DELIVERABLE_ADDITIONAL_COSTS, DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION } from 'utils/deliverables';

import type { Params } from 'types';
import type {
  BudgetSplitResource,
  BudgetSplitResourceV2,
  CalculatedBudgetSplitResource
} from '../../types/BudgetSplitResource.type';

import './reporting-cost-attribution-page.scss';

applyTranslations(require('./reporting-cost-attribution-page.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));


type MappedSplitState = {
  [x: string]: string
};

type GlobalMappedSplitState = {
  [x: string]: MappedSplitState
};

type OwnProps = {
  params: Params
};

type StateProps = {
  splits: Array<BudgetSplitResourceV2>,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchSplits: (campaignId: number) => void,
  dispatchResetSplits: () => void,
  dispatchUpdateSplits: (currentSplits: GlobalMappedSplitState) => Promise<void>,
  dispatchShowConfirmModal: (callbackFn: () => void) => void,
  dispatchFetchMetaInfo: (campaignId: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: CostAttributionBulkModal
});

function getMessengerUrl(campaignId: string, subcampaignId: number): string {
  return `/campaigns/${campaignId}/messages?subcampaignId=${subcampaignId}`;
}

function getOfferUrl(campaignId: string, subcampaignId: number): string {
  return `/campaigns/${campaignId}/subcampaigns/${subcampaignId}`;
}

function mapCalculatedSplitToState(resource: CalculatedBudgetSplitResource): MappedSplitState {
  const result = {
    [DELIVERABLE_ADDITIONAL_COSTS]: String(resource.additionalCosts),
    [DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION]: resource.additionalCostsDetail
  };

  resource.taskSplits.forEach((split) => {
    result[String(split.deliverableType)] = String(split.budget);
  });

  return result;
}

function mapSplitToState(resource: BudgetSplitResource): MappedSplitState {
  const additionalCosts = resource.isOutdated ? 0 : (resource.additionalCosts ?? 0);
  const additionalCostsDetail = resource.isOutdated ? '' : (resource.additionalCostsDetail ?? '');

  const result = {
    [DELIVERABLE_ADDITIONAL_COSTS]: String(additionalCosts),
    [DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION]: additionalCostsDetail
  };

  resource.taskSplits.forEach((split) => {
    if (split.count > 0) {
      const budget = resource.isOutdated ? 0 : (split.budget ?? 0);
      result[String(split.deliverableType)] = String(budget);
    }
  });

  return result;
}

function getGlobalSplitState(splits: Array<BudgetSplitResourceV2>): GlobalMappedSplitState {
  const result = {};

  splits.forEach((split) => {
    result[String(split.creator.id)] = mapSplitToState(split.budgetSplit);
  });

  return result;
}

const CostAttributionPage = (
  {
    params,
    splits,
    isLoading,
    dispatchFetchSplits,
    dispatchResetSplits,
    dispatchUpdateSplits,
    dispatchFetchMetaInfo,
    dispatchShowConfirmModal
  }: Props
): JSX.Element => {
  const campaignId = Number(params.id);

  const [search, setSearch] = React.useState('');
  const [wasStateUpdate, setStateUpdated] = React.useState(false);
  const [currentSplits, setSplits] = React.useState(getGlobalSplitState(splits));
  const [lastStateReset, setLastStateReset] = React.useState(Date.now());

  React.useEffect(() => {
    dispatchFetchSplits(campaignId);

    return dispatchResetSplits;
  }, [campaignId]);

  React.useEffect(() => {
    // update state on props update
    setSplits(getGlobalSplitState(splits));
    setStateUpdated(false);
  }, [splits]);

  const elementIdToScroll = window.location.hash.substr(1);
  useScrollTo(elementIdToScroll);

  React.useEffect(() => {
    if (!elementIdToScroll) {
      // if has no hash - scroll to first required
      setTimeout(autoscrollToRequired, 500, true);
    }
  }, []);

  const hasData = !isLoading
    && splits.filter(split => split.budgetSplit.taskSplits.some((entry) => entry.count > 0)).length > 0;
  const title = __t('reporting_cost_attribution_page.title');

  if (!isLoading && !hasData) {
    return (
      <BaseReportingPage
        headline={title}
        params={params}
      >
        <section className="reporting-cost-attribution-page__empty-message">
          {__t('reporting_cost_attribution_page.no_data_error')}
        </section>
      </BaseReportingPage>
    );
  }

  const actionButtons = (
    <React.Fragment>
      <ActionButton
        color="tertiary"
        disabled={!wasStateUpdate}
        onClick={() => {
          dispatchShowConfirmModal(() => {
            setSearch('');
            setSplits(getGlobalSplitState(splits));
            setStateUpdated(false);
            setLastStateReset(Date.now());
          });
        }}
      >
        {__t('collabary_common.action_button.cancel')}
      </ActionButton>
      <ActionButton
        type="submit"
        color="primary"
      >
        {__t('reporting_cost_attribution_page.save_button_label')}
      </ActionButton>
    </React.Fragment>
  );

  return (
    <BaseReportingPage
      headline={title}
      params={params}
    >
      {
        wasStateUpdate &&
        <PromptExit
          type="page"
          title={__t('reporting_cost_attribution_page.exit_modal_title')}
          message={__t('reporting_cost_attribution_page.exit_modal_description')}
          actionButtonLabel={__t('reporting_cost_attribution_page.exit_modal_action_label')}
        />
      }
      <div className="reporting-cost-attribution-page__title">
        {title}
      </div>

      <VerticalSpacing type="large" />

      <CCAMetricsSummary
        splits={splits}
      />

      <VerticalSpacing type="xlarge" />

      <div className="reporting-cost-attribution-page__description">
        {__t('reporting_cost_attribution_page.description_label')}
        &nbsp;
        <Popover
          classNameTrigger="reporting-cost-attribution-page__description-popover-trigger"
          triggerComponent={(
            <SVGDynamicIcon
              name="question-mark-inside-circle-custom-bg-white-fg"
              width={12}
              height={12}
              customColor="grey-light"
            />
          )}
        >
          <div className="reporting-cost-attribution-page__description-popover">
            <div>{__t('reporting_cost_attribution_page.description_tooltip_a')}</div>
            <VerticalSpacing type="small" />
            <div>{__t('reporting_cost_attribution_page.description_tooltip_b')}</div>
          </div>
        </Popover>
        <div className="reporting-cost-attribution-page__bulk-operations">
          <ActionButtonWithModal
            modalContentProps={{
              splits,
              campaignId,
              onReady: (data: Array<CalculatedBudgetSplitResource>) => {
                const newState = data.reduce((result, entry) => {
                  const influencerId = splits
                    .find((split) => split.budgetSplit.offerId === entry.offerId)
                    ?.creator.id;

                  if (!influencerId) {
                    return currentSplits;
                  }

                  return ({
                    ...result,
                    [influencerId]: mapCalculatedSplitToState(entry)
                  });
                }, currentSplits);

                setSplits(newState);
                setStateUpdated(!isEqual(newState, getGlobalSplitState(splits)));
                setLastStateReset(Date.now());
              }
            }}
          >
            {__t('reporting_cost_attribution_page.bulk_operation_label')}
          </ActionButtonWithModal>
        </div>
      </div>

      <VerticalSpacing type="large" />

      <Form
        onSubmit={() => {
          setSearch('');
          dispatchUpdateSplits(currentSplits)
            .then(() => {
              setStateUpdated(false);
              dispatchFetchSplits(campaignId);
              dispatchFetchMetaInfo(campaignId);
            });
        }}
        onValidationErrors={() => {
          setSearch('');
        }}
      >
        <section className="reporting-cost-attribution-page__actions">
          <CCACreatorSearch
            value={search}
            onChange={setSearch}
          />
          <div>
            {actionButtons}
          </div>
        </section>

        <VerticalSpacing type="large" />

        {
          isLoading && (
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          )
        }
        {
          !isLoading && splits
            .filter((split) => currentSplits[String(split.creator.id)])
            .map((split) => {
              const deliverables = split.budgetSplit.taskSplits
                .filter((entry) => entry.count > 0)
                .map(({ deliverableType, count }) => ({ deliverableType, count }));

              const isHidden = !split.creator.name.toLowerCase().includes(search);
              const classNames = classnames('reporting-cost-attribution-page__tile', {
                'reporting-cost-attribution-page__tile--hidden': isHidden
              });

              return (
                <div
                  key={`${split.budgetSplit.offerId}-${lastStateReset}`}
                  id={`creator-tile-${split.creator.id}`}
                  className={classNames}
                >
                  <CostAttributionTile
                    splits={currentSplits[String(split.creator.id)]}
                    creator={split.creator}
                    totalAgreedBudget={split.budgetSplit.totalBudget}
                    deliverables={deliverables}
                    linkToMessenger={getMessengerUrl(params.id, split.budgetSplit.subcampaignId)}
                    linkToOffer={getOfferUrl(params.id, split.budgetSplit.subcampaignId)}
                    onChange={(newAttribution) => {
                      const newState = {
                        ...currentSplits,
                        [String(split.creator.id)]: newAttribution
                      };
                      setSplits(newState);

                      setStateUpdated(!isEqual(newState, getGlobalSplitState(splits)));
                    }}
                  />
                </div>
              );
            })
        }
        <div className="reporting-cost-attribution-page__bottom-actions">
          {actionButtons}
        </div>
      </Form>
    </BaseReportingPage>
  );
};

const mapStateToProps = ({ campaignCostAttribution }): StateProps => ({
  splits: campaignCostAttribution.results,
  isLoading: campaignCostAttribution.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSplits: (campaignId: number) => dispatch(fetchSplits(campaignId)),
  dispatchResetSplits: () => dispatch(resetSplits()),
  dispatchUpdateSplits: (attribution: GlobalMappedSplitState) => dispatch(updateSplits(attribution)),
  dispatchFetchMetaInfo: (campaignId: number) => dispatch(fetchMetaInfo(campaignId)),

  dispatchShowConfirmModal: (onConfirm: () => void) =>
    dispatch(
      showConfirmModal({
        title: __t('reporting_cost_attribution_page.exit_modal_title'),
        message: __t('reporting_cost_attribution_page.exit_modal_description'),
        confirmButtonLabel: __t('reporting_cost_attribution_page.exit_modal_action_label'),
        onCancel: () => dispatch(hideConfirmModal()),
        onConfirm: () => {
          onConfirm();
          dispatch(hideConfirmModal());
        }
      })
    )
});

export default connect(mapStateToProps, mapDispatchToProps)(CostAttributionPage);
